import React from "react";
import { Text, Card, Flex } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { Money } from "~/components/Money";
import { type ProcessingPaymentsWithConfirmationStatusQuery } from "~/graphql/supergraphTypes";
import { toDbId } from "~/utils/graphql-helpers";
import { FieldRow } from "../../utils/FieldRow";
import { formatFieldValue } from "../../utils/formatFieldValue";
import ProcessingPaymentCard from "../ProcessingPaymentCard";
import * as styles from "./styles_vanilla.css";

type ProcessingPaymentBatchWithProviderStatusType =
  ProcessingPaymentsWithConfirmationStatusQuery["processingPaymentsWithConfirmationStatus"][0];

interface ProcessingPaymentBatchCardProps {
  batchData: ProcessingPaymentBatchWithProviderStatusType;
}

const ProcessingPaymentBatchCard: React.FC<ProcessingPaymentBatchCardProps> = ({
  batchData,
}) => {
  const { paymentBatch, payments } = batchData;

  return (
    <Card className={styles.batchCardWrapper}>
      <Flex flexDirection="column" gap={Sizes.MD} className={styles.batchCardContent}>
        <Text variant="strong-lg">Payment Batch: {paymentBatch.id}</Text>

        <Flex flexDirection="column" gap={Sizes.SM}>
          <FieldRow label="Batch ID:" value={formatFieldValue(paymentBatch.id)} />
          <FieldRow label="Status:" value={formatFieldValue(paymentBatch.status)} />
          <FieldRow label="Created At:" value={formatFieldValue(paymentBatch.createdAt)} />
          <FieldRow
            label="Creator ID:"
            value={formatFieldValue(toDbId(paymentBatch.creatorId))}
          />
          <FieldRow
            label="Object ID:"
            value={formatFieldValue(toDbId(paymentBatch.objectId))}
          />
          <FieldRow
            label="Net Amount:"
            value={<Money money={paymentBatch.netAmount} rounded={false} />}
          />
          <FieldRow
            label="Objects Count:"
            value={formatFieldValue(paymentBatch.objectsCount)}
          />
          <FieldRow
            label="Payment Description:"
            value={formatFieldValue(paymentBatch.paymentDescription)}
          />
          <FieldRow
            label="Required Approval Count:"
            value={formatFieldValue(paymentBatch.requiredApprovalCount)}
          />
          <FieldRow
            label="Different Approver Required:"
            value={formatFieldValue(paymentBatch.isDifferentApproverRequired)}
          />
          <FieldRow label="Canceled At:" value={formatFieldValue(paymentBatch.canceledAt)} />
          <FieldRow
            label="Canceled By User ID:"
            value={
              paymentBatch.canceledByUserId
                ? formatFieldValue(toDbId(paymentBatch.canceledByUserId))
                : "null"
            }
          />
        </Flex>

        <Flex flexDirection="column" gap={Sizes.MD}>
          <Text variant="strong-md">Processing Payments ({payments.length})</Text>
          {payments.length > 0 ? (
            <Flex flexDirection="column" gap={Sizes.MD}>
              {payments.map((paymentData, index) => (
                <ProcessingPaymentCard
                  key={paymentData.id}
                  paymentData={paymentData}
                  paymentIndex={index + 1}
                />
              ))}
            </Flex>
          ) : (
            <Text variant="regular-sm">No processing payments in this batch</Text>
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

export default ProcessingPaymentBatchCard;
export { ProcessingPaymentBatchCard };
export type { ProcessingPaymentBatchCardProps };
